import { Badge } from '@/components/ui';
import type { CallOutcome, ClosedReason, Prospect, TouchChannel } from '@/data/types';

export const CHANNELS: { value: TouchChannel; label: string; icon: string }[] = [
  { value: 'phone', label: 'Call', icon: 'phone' },
  { value: 'sms', label: 'Text', icon: 'message-square' },
  { value: 'email', label: 'Email', icon: 'mail' },
];

type Outcome = { value: CallOutcome; label: string; hint: string };

const MESSAGE_OUTCOMES = (bounce: string): Outcome[] => [
  { value: 'no_reply', label: 'Sent, no reply', hint: 'Counts as an attempt' },
  { value: 'replied', label: 'They replied', hint: 'Reached them — resets the count; pick a follow-up time if they gave one' },
  { value: 'not_interested', label: 'Not interested', hint: 'Closes the prospect' },
  { value: 'bad_number', label: bounce, hint: 'Closes the prospect' },
  { value: 'do_not_contact', label: 'Do not contact', hint: 'Closes it and blocks the domain' },
];

/** Outcomes per channel. Calls, texts and emails all move the same rotation (worker/prospecting/cadence.ts). */
export const OUTCOMES: Record<TouchChannel, Outcome[]> = {
  phone: [
    { value: 'no_answer', label: 'No answer', hint: 'Counts as an attempt' },
    { value: 'voicemail', label: 'Voicemail', hint: 'Counts as an attempt' },
    { value: 'gatekeeper', label: 'Gatekeeper', hint: 'Didn\'t reach the decision-maker' },
    { value: 'callback', label: 'Call back', hint: 'Reached them — pick a time' },
    { value: 'not_interested', label: 'Not interested', hint: 'Closes the prospect' },
    { value: 'bad_number', label: 'Wrong number', hint: 'Closes the prospect' },
    { value: 'do_not_contact', label: 'Do not contact', hint: 'Closes it and blocks the domain' },
  ],
  sms: MESSAGE_OUTCOMES('Wrong number'),
  email: MESSAGE_OUTCOMES('Bounced'),
};

export const CLOSE_REASONS: { value: ClosedReason; label: string }[] = [
  { value: 'not_interested', label: 'Not interested' }, { value: 'not_a_fit', label: 'Not a fit' },
  { value: 'do_not_contact', label: 'Do not contact' }, { value: 'bad_number', label: 'Wrong number' },
  { value: 'existing_client', label: 'Existing client' }, { value: 'other', label: 'Other' },
];

/** Icon for a prospect's history entry (calls, texts, emails, notes, status changes). */
export const HISTORY_ICON: Record<string, string> = { call: 'phone', text: 'message-square', email: 'mail', note: 'edit' };

export const attemptsLabel = (n: number) => `${n} attempt${n === 1 ? '' : 's'}`;

export function ProspectStatusBadge({ p }: { p: Pick<Prospect, 'status' | 'closedLabel'> }) {
  switch (p.status) {
    case 'new': return <Badge variant="accent">New</Badge>;
    case 'active': return <Badge variant="warning">In rotation</Badge>;
    case 'cold': return <Badge variant="neutral">Cold</Badge>;
    case 'converted': return <Badge variant="success">Lead</Badge>;
    default: return <Badge variant="outline">Closed{p.closedLabel ? ` · ${p.closedLabel}` : ''}</Badge>;
  }
}
